import { useEffect } from "react";
import { useReferralStore } from "./useReferralStore";
import { useConfig } from "../config";
import { Capacitor } from "@capacitor/core";
import { Clipboard } from "@capacitor/clipboard";
import { useAuth } from "../../contexts/auth-context";

const checkClipboardForReferralLink = async (baseUrl: string) => {
  const referralLink = `${baseUrl}/customer-referrals?referralId`;

  let clipboardReferralId = null;

  // Check if on a native platform and Clipboard is available
  if (Capacitor.isNativePlatform() && Clipboard) {
    console.log("native platform");

    try {
      const { value } = await Clipboard.read();
      if (value && value.includes(referralLink)) {
        clipboardReferralId = value.split("=")[1];
      }
    } catch (error: any) {
      const message = error?.message ?? "";
      if (message.includes("no data on the clipboard")) {
        return;
      }
      console.error("Failed to read clipboard:", message);
    }
  }

  // Check if navigator.clipboard API is available
  else if (navigator && navigator.clipboard && typeof navigator.clipboard.readText === "function") {
    console.log("web with clipboard api");

    try {
      const clipboardData = await navigator.clipboard.readText();
      if (clipboardData && clipboardData.includes(referralLink)) {
        clipboardReferralId = clipboardData.split("=")[1];
      }
    } catch (error: any) {
      console.error("Clipboard access error:", error?.message);

      // Don't attempt fallback if permission was denied
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        console.log("Clipboard access was denied. User interaction may be required.");
        return;
      }
    }
  }

  // Fallback for older browsers
  else {
    console.log("fallback for older browsers");
    try {
      const selection = document.getSelection();
      if (selection && selection.toString().includes(referralLink)) {
        clipboardReferralId = selection.toString().split("=")[1];
      }
    } catch (error: any) {
      console.error("Selection fallback error:", error?.message);
    }
  }

  return clipboardReferralId;
};

export const useInitReferralFromClipboard = () => {
  const config = useConfig();
  const user = useAuth();
  const { setReferralId: setReferral, referralId } = useReferralStore();

  useEffect(() => {
    if (user?.isUserAuthenticated || referralId) return;

    const handleClipboard = async () => {
      const referralId = await checkClipboardForReferralLink(config.baseUrl);

      if (referralId) {
        setReferral(referralId);
      }
    };

    handleClipboard();
  }, [setReferral]);
};
